import './drawing-editor.scss'

import { defineComponent, PropType } from 'vue'
import _ from 'lodash'
import { forkJoin, from, Observable, of } from 'rxjs'
import { catchError, map, switchMap, take } from 'rxjs/operators'
import { faArrowLeft, faCheck, faCircleNotch, faLongArrowAltLeft, faPalette, faPen, faEraser, faHandPointer } from '@fortawesome/free-solid-svg-icons'
import { faCircle, faImage, faSquare, faTimesCircle } from '@fortawesome/free-regular-svg-icons'

import { Alteration, createTranslatedString, editTranslatedString, getTranslatedString, Media as MediaType, MediaUploadResult2} from '@skinsoft/types'
import { isImageOrientationAutoManaged, mergeOverridingArrays } from '@skinsoft/utils'
import { arrayBufferToBase64, extractAndRemoveExifOrientationFromMedia, getImageOrientation, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { thesaurusTermSelectors } from '@skinsoft/selectors'
import { MediaDataStore } from '@skinsoft/entity'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { Media } from '@skinsoft/ui/src/item-card/media'
import { ArrowDrawingTool, CircleDrawingTool, DrawingTool, DrawingToolType, EraserDrawingTool, FreeDrawingTool, HandDrawingTool, RectangleDrawingTool, ShapeConfig } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'
import { CLOSE_DRAWING_EDITOR_TOPIC } from '../../../../busTopics'
import { AnnotationDrawing } from '../annotation-drawing'
import { ColorSwatchPicker } from '../color-swatch-picker'
import { ALTERATION_EDITED, AlterationChanged, DELETE_ALTERATION } from '../alteration-line'
import { LocalizedString } from '@skinsoft/ui'
import AlterationList from '../../alteration-area/alteration-list'
import { CANCEL_ALL_IMAGE_UPLOAD, CANCEL_IMAGE_UPLOAD, RETRY_ALL_IMAGE_UPLOAD, RETRY_IMAGE_UPLOAD } from '@skinsoft/ui2'
import { FailedImageUpload, UploadImageFailureDialog } from '@skinsoft/ui2/error-handling/upload-image-failure-dialog'
import Konva from 'konva'

declare const config

const MAX_IMAGE_UPLOAD_TRY = 6
const IMAGE_UPLOAD_RETRY_DELAY = 2000
const DEFAULT_ZOOM_SCALE = 1.01
const DEBUG_SHOW_ERASER_AREA = config.drawingArea.tools.eraser.debugShowArea

export interface DrawingEditorClosed {
    saveReport: boolean,
    newData?: Alteration[],
    alterationMediaId?: MediaType["id"],
}

enum ZoomDirection {
    IN,
    OUT
}

export const DrawingEditor = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sAnnotationDrawing: AnnotationDrawing,
        sMedia: Media,
        sAlterationList: AlterationList,
        sColorSwatchPicker: ColorSwatchPicker,
        sLocalizedString: LocalizedString,
        sUploadImageFailureDialog: UploadImageFailureDialog,
    },
    props: {
        mediaId: { type: String, required: true },
        mediaAlterations: { type: Array as PropType<Alteration[]>, default: () => [] },
        mediaUrl: { type: String },
        readOnly: { type: Boolean },
        existingTitles: {
            type: Array as PropType<String[]>,
            default:() => []
        },
        firstAlterationIndex: {
            type: Number,
            default: 1,
        },
    },
    template: require('./drawing-editor.html'),
    data() {
        // Both eraserOutlineConfig and strokeColor initialize from a sibling data property below;
        // `this` isn't available yet while data() is building its return value, so local consts stand in.
        const DEFAULT_STROKE_COLOR = "#f15f22"
        const eraserRadius = 50

        return {
            isOnline: window.navigator.onLine, // TEMPORARY to disable detailed view offline

            // Icons
            backIcon: faArrowLeft,
            circleIcon: faCircle,
            arrowIcon: faLongArrowAltLeft,
            curveIcon: faPen,
            squareIcon: faSquare,
            crossCircleIcon: faTimesCircle,
            checkIcon: faCheck,
            colorIcon: faPalette,
            imageIcon: faImage,
            loadingIcon: faCircleNotch,
            eraserIcon: faEraser,
            handIcon: faHandPointer,

            alterationsCopy: [] as Alteration[],
            mediaNotice: null as MediaType | null,
            isUploadingMedias: false,
            failedImageUploads: [] as FailedImageUpload[],
            uploadFailureDescription: '',

            image: new Image(),
            configImage: {} as any,
            configTransformer: {
                borderEnabled: false,
                enabledAnchors: [],
                anchorSize: 0,
            } as Konva.TransformerConfig,

            // Drawing
            DEFAULT_STROKE_COLOR: DEFAULT_STROKE_COLOR,
            strokeColor: DEFAULT_STROKE_COLOR,
            // vue3-swatches dropped the named presets vue-swatches shipped; this is the exact
            // "material-basic" palette the previous library exposed.
            swatchColors: [
                "#F44336", "#E91E63", "#9C27B0", "#673AB7", "#3F51B5", "#2196F3", "#03A9F4",
                "#00BCD4", "#009688", "#4CAF50", "#8BC34A", "#CDDC39", "#FFEB3B", "#FFC107",
                "#FF9800", "#FF5722", "#795548", "#9E9E9E", "#607D8B",
            ],
            currentToolType: DrawingToolType.HAND as DrawingToolType,
            currentTool: null as any as DrawingTool,
            selectedToolClass: "selectedTool",
            toolBox: new Map<DrawingToolType, DrawingTool>(),
            drawings: [] as ShapeConfig[][],
            debugDrawings: [] as ShapeConfig[][],
            currentDrawing: undefined as any,
            currentDebugDrawing: undefined as any,
            container: null as any,
            stage: null as any,
            stageScaleFactor: 1,
            stageOffset: { x: 0, y: 0 },
            startClick: undefined as any,
            isDrawing: false,
            orientation: 1,
            isDrawingFirstShape: true,

            // Responsive computing default values
            strokeWidth: 5,
            arrowPointerWidth: 20,
            arrowPointerLength: 20,
            annotationLabelConfig: {
                fontSize: 18 as number,
                radius: 15 as number,
                strokeWidth: 2 as number
            },
            editorOrientationClass: "",
            canvasOrientationClass: "",

            eraserRadius: eraserRadius,
            eraserOutlineConfig: {
                fill: "black",
                opacity: 0.5,
                stroke: "white",
                strokeWidth: 5,

                radius: eraserRadius,
                x: -10,
                y: -10,
            },

            // The ratios represent a relative size compared to the image's width, so that
            // the apparent size of annotation and labels is the same for all kind of image dimensions
            STROKE_WIDTH_RATIO: 0.005,
            ANNOTATION_LABEL_RADIUS_RATIO: 0.02,
            ANNOTATION_LABEL_FONT_SIZE_RATIO: 0.02,
            ANNOTATION_LABEL_STROKE_WIDTH_RATIO: 0.002,
            ARROW_POINTER_LENGTH_RATIO: 0.02,
            ARROW_POINTER_WIDTH_RATIO: 0.02,

            ready: false,

            // The factor used to limit the number of points in a free drawing
            //private freeDrawingSimplificationFactor: number = 1;
            FREE_DRAWING_SIMPLIFICATION_FACTOR_RATIO: 0.002,

            configKonva: {
                height: 50,
                width: 50,
            },

            lastPinchCenter: null as {x: number, y: number} | null,
            lastPinchDistance: null as number | null,

            DELETE_ALTERATION: DELETE_ALTERATION,
            ALTERATION_EDITED: ALTERATION_EDITED,
            RETRY_ALL_IMAGE_UPLOAD: RETRY_ALL_IMAGE_UPLOAD,
            CANCEL_ALL_IMAGE_UPLOAD: CANCEL_ALL_IMAGE_UPLOAD,
            RETRY_IMAGE_UPLOAD: RETRY_IMAGE_UPLOAD,
            CANCEL_IMAGE_UPLOAD: CANCEL_IMAGE_UPLOAD,
        }
    },
    mounted() {
        // TEMPORARY to disable detailed view offline
        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)

        this.fetchMedia();
        this.fetchMediaNotice()
        this.alterationsCopy = _.cloneDeep(this.mediaAlterations)

        // getting drawings
        this.changeDrawingTool(DrawingToolType.HAND)
        this.chooseTool(DrawingToolType.HAND)
        this.drawings = this.mediaAlterations.map(alteration => {
            try {
                if(alteration.drawing) {
                    return JSON.parse(alteration.drawing)
                }
                return []
            } catch {
                return []
            }
        })
        if (!this.drawings) {
            this.drawings = []
        }
        this.renumberShapes()
    },
    computed: {
        legendOrientationClass(): string {
            return this.editorOrientationClass === 'drawing-editor-v' ?
                'drawing-editor--vertical' :
                'drawing-editor--horizontal'
        },

        mediaLegend: {
            get(): string {
                if(this.mediaNotice?.mediaCS?.legendMedia && this.mediaNotice.mediaCS?.legendMedia.length) {
                    if(this.mediaNotice.mediaCS.legendMedia[0].legend) {
                        return getTranslatedString(this.mediaNotice.mediaCS.legendMedia[0].legend, this.getAppLocale())
                    }
                }

                return ''
            },
            set(legend: string) {
                if(this.mediaNotice?.mediaCS) {
                    if(this.mediaNotice.mediaCS.legendMedia && this.mediaNotice.mediaCS.legendMedia.length) {
                        if(this.mediaNotice.mediaCS.legendMedia[0].legend) {
                            editTranslatedString(
                                this.mediaNotice.mediaCS.legendMedia[0].legend,
                                legend,
                                this.getAppLocale()
                            )
                        } else {
                            _.set(
                                this.mediaNotice,
                                'mediaCS.legendMedia[0].legend',
                                createTranslatedString(this.getAppLocale(), legend)
                            )
                        }

                    } else {
                        _.set(
                            this.mediaNotice,
                            'mediaCS.legendMedia',
                            [
                                {
                                    legend: createTranslatedString(this.getAppLocale(), legend),
                                },
                            ]
                        )
                    }
                }
            },
        },

        canResizeCurrentTool(): boolean {
            return this.currentToolType === DrawingToolType.ERASER
        },
    },
    methods: {
        // TEMPORARY to disable detailed view offline
        updateConnexionStatus() {
            this.isOnline = window.navigator.onLine
        },

        fetchMedia() {
            if(this.mediaUrl) {
                this.setupMedia(this.mediaUrl)
            } else {
                if(window.navigator.onLine || this.mediaId.startsWith('local:')) {
                    this.subscribeTo(this.$imageTransferHelper.downloadImage(this.mediaId as string, "proxy"),
                        mediaSrc => {
                            this.setupMedia(mediaSrc.dataUrl)
                        },
                        'media',
                    );

                } else {
                    this.subscribeTo(this.$imageTransferHelper.downloadImageOffline(this.mediaId as string),
                        mediaSrc => {
                            this.setupMedia(mediaSrc.dataUrl)
                        },
                        'media',
                    )
                }
            }
        },

        fetchMediaNotice() {
            if(this.mediaId && navigator.onLine) {
                MediaDataStore.getInstance().fetchOne(this.mediaId as string).subscribe(mediaNotice => {
                    this.mediaNotice = mediaNotice
                })
            }
        },

        closeDrawingEditor(save: boolean) {
            let closeEventData: DrawingEditorClosed = {
                saveReport: save
            }
            if(save) {
                closeEventData.newData = this.alterationsCopy
                closeEventData.alterationMediaId = this.mediaId as string
                if(this.mediaNotice) {
                    MediaDataStore.getInstance().update(this.mediaNotice.id, this.mediaNotice).subscribe()
                }
            }

            window.removeEventListener('resize', this.resizeStage);
            this.emitOnBus(CLOSE_DRAWING_EDITOR_TOPIC, closeEventData)
        },

        getThesaurusTermValue(thesaurusTermId: string): Promise<string> {
            return this.$thesaurusTermDataStore.fetchOne(thesaurusTermId).pipe(
                map(thesaurusTerm => {
                    return getTranslatedString(
                        thesaurusTermSelectors.preferredTerm(thesaurusTerm),
                        this.getAppLocale()
                    )
                }),
                catchError(err => {
                    return ''
                })
            ).toPromise()
        },

        addAlteration() {
            let newAlteration: Alteration = {
                media: this.mediaId as string,
                drawing: '',
                detailedViews: [],
                //@ts-ignore
                typeOfAlteration: null,
                precision: createTranslatedString(this.getAppLocale(), ''),
                title: createTranslatedString(this.getAppLocale(), ''),
            }
            this.alterationsCopy.push(newAlteration)
        },

        deleteAlteration(alterationNumber) {
            const indexInArray = alterationNumber - (this.firstAlterationIndex as number)
            this.drawings.splice(indexInArray, 1)
            this.alterationsCopy.splice(indexInArray, 1)
        },

        onAlterationChanged(event: AlterationChanged) {
            const indexInArray = event.alterationNumber - (this.firstAlterationIndex as number)
            const oldValue = this.alterationsCopy[indexInArray]
            this.alterationsCopy[indexInArray] = mergeOverridingArrays(oldValue, event.updates) as Alteration
        },

        validate() {
            const valid = this.alterationsCopy.every(alteration => {
                const title = getTranslatedString(alteration.title, this.getAppLocale())
                const type =  alteration.typeOfAlteration

                return (title || type)
            })

            if(valid) {
                if(window.navigator.onLine) {
                    const theRealThis = this
                    this.handleUploadDetailedViews(() => {
                        theRealThis.closeDrawingEditor(true)
                    })
                } else {
                    this.closeDrawingEditor(true)
                }
            } else {
                const alertPayload = {
                    message: this.$t('drawingEditor.errors.missingTitleOrType'),
                    variant: "warning"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            }
        },

        handleUploadDetailedViews(onSuccess: () => void) {
            this.isUploadingMedias = true

            const theRealThis = this

            this.uploadDetailedViews().subscribe(async uploadResults => {
                theRealThis.failedImageUploads = []
                let failedUploads: {[cacheKey: string]: FailedImageUpload} = {}
                let validUploads: {[cacheKey: string]: MediaUploadResult2 | undefined} = {}
                let alterationContainingFailedUpload: number[] = []

                // partition upload results between success and failure
                for(const uploadResult of uploadResults) {
                    if(!uploadResult.errorOccured && uploadResult.mediaId && uploadResult.cacheKey) {
                        validUploads[uploadResult.cacheKey] = uploadResult
                    } else if(uploadResult.cacheKey) {
                        const base64 = await theRealThis.$imageTransferHelper
                            .getCachedMediaValueAsBase64(uploadResult.cacheKey).pipe(
                                take(1), // needed for promise to resolve (the observable must complete)
                            )
                            .toPromise()

                        // check if base64 match mongo id url, if so it is the id of the uploaded image
                        // and the uploadResult is valid, we just have to add mediaId to the uploadResult
                        // and put it inside validUploads

                        failedUploads[uploadResult.cacheKey] = {
                            mediaUrl: base64,
                            cacheKey: uploadResult.cacheKey,
                        }
                    }
                }

                // replace cache key with mediaId for success uploads and add error for failed uploads
                if(Object.keys(validUploads).length || Object.keys(failedUploads).length) {
                    for(let [i, alteration] of theRealThis.alterationsCopy.entries()) {
                        if(alteration.detailedViews?.length) {
                            alteration.detailedViews = await Promise.all(
                                alteration.detailedViews.map(async mediaId => {
                                    const uploadResult = validUploads[mediaId]
                                    if(uploadResult) {
                                        return uploadResult.mediaId! // ! because we made sure that mediaId is present in validUploads
                                    }

                                    let failedUpload = failedUploads[mediaId]
                                    if(failedUpload) {
                                        const title = alteration.title ? getTranslatedString(alteration.title, theRealThis.getAppLocale()) : ''
                                        let typeOfAlteration = ''
                                        if(alteration.typeOfAlteration) {
                                            typeOfAlteration = await theRealThis.getThesaurusTermValue(alteration.typeOfAlteration)
                                        }

                                        failedUpload.info = this.$t('drawingEditor.errors.alteration') as string + (i + 1) + ': ' + (title ? title + ' - ' : '') + typeOfAlteration
                                        alterationContainingFailedUpload.push(i + 1)
                                        theRealThis.failedImageUploads.push(failedUpload)
                                    }

                                    return mediaId
                                })
                            )
                        }
                    }
                }

                theRealThis.isUploadingMedias = false

                if(theRealThis.failedImageUploads.length) {
                    theRealThis.uploadFailureDescription = theRealThis.computeUploadFailureDescription(alterationContainingFailedUpload)
                    theRealThis.openImageUploadFailureDialog()
                } else {
                    onSuccess()
                }
            })
        },

        uploadDetailedViews(): Observable<MediaUploadResult2[]> {
            if(window.navigator.onLine) {
                let mediaUploads$: Observable<MediaUploadResult2>[] = []

                for(const alteration of this.alterationsCopy) {
                    for(const mediaId of alteration.detailedViews ?? []) {
                        if(MediaTransferHelper.isLocalMedia(mediaId)) {
                            mediaUploads$.push(this.uploadDetailedView(mediaId))
                        }
                    }
                }

                if(mediaUploads$.length) {
                    return forkJoin(mediaUploads$)
                }
            }

            return of([] as MediaUploadResult2[])
        },

        uploadDetailedView(cacheKey: string): Observable<MediaUploadResult2> {
            return this.$imageTransferHelper.uploadLocalImageToApi(cacheKey).pipe(
                switchMap(uploadResult => {
                    if(uploadResult.fileName) {
                        return from(
                            this.$imageTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(
                                uploadResult.fileName,
                                MAX_IMAGE_UPLOAD_TRY,
                                IMAGE_UPLOAD_RETRY_DELAY
                            )
                        ).pipe(
                            map(media => {
                                if(media.id) {
                                    return {
                                        ...uploadResult,
                                        mediaId: media.id,
                                    }
                                }
                                return uploadResult
                            }),
                            catchError(err => {
                                return of({
                                    ...uploadResult,
                                    timeOut: true,
                                })
                            }),
                        )
                    }

                    return of(uploadResult)
                }),
            )
        },

        openImageUploadFailureDialog() {
            (this.$refs.uploadImageFailureDialog as InstanceType<typeof UploadImageFailureDialog>).openModal()
        },

        onRetryUploadAllImages() {
            (this.$refs.uploadImageFailureDialog as InstanceType<typeof UploadImageFailureDialog>).closeModal()
            this.validate()
        },

        onCancelUploadAllImages() {
            const mediaToRemoveCacheKey = this.failedImageUploads.map(failedUpload => failedUpload.cacheKey)

            for(let alteration of this.alterationsCopy) {
                if(alteration.detailedViews?.length) {
                    alteration.detailedViews = alteration.detailedViews.filter(mediaId => {
                        return !mediaToRemoveCacheKey.includes(mediaId)
                    })
                }
            }

            (this.$refs.uploadImageFailureDialog as InstanceType<typeof UploadImageFailureDialog>).closeModal()
        },

        onRetryUploadImage(index: number) {
            const failedUpload = this.failedImageUploads[index]
            this.failedImageUploads[index] = {...failedUpload, currentlyUploading: true} as FailedImageUpload

            this.uploadDetailedView(failedUpload.cacheKey).subscribe(uploadResult => {
                if(!uploadResult.errorOccured && uploadResult.mediaId && uploadResult.cacheKey) {
                    const alterationsWithFailedUpload: number[] = []

                    for(let [i, alteration] of this.alterationsCopy.entries()) {
                        if(alteration.detailedViews?.length) {
                            let hasCachedMedias = false

                            alteration.detailedViews = alteration.detailedViews.map(mediaId => {
                                if(mediaId === uploadResult.cacheKey) {
                                    return uploadResult.mediaId! // ! because we checked that uploadResult.mediaId is not null before
                                }

                                if(MediaTransferHelper.isLocalMedia(mediaId)) {
                                    hasCachedMedias = true
                                }
                                return mediaId
                            })

                            if(hasCachedMedias) {
                                alterationsWithFailedUpload.push(i + 1)
                            }
                        }
                    }

                    this.failedImageUploads.splice(index, 1)
                    this.uploadFailureDescription = this.computeUploadFailureDescription(alterationsWithFailedUpload)
                } else {
                    this.failedImageUploads[index] = {...failedUpload, currentlyUploading: false} as FailedImageUpload
                }
            })
        },

        onCancelUploadImage(index: number) {
            const failedUpload = this.failedImageUploads[index]
            const alterationsWithFailedUpload: number[] = []

            for(let [i, alteration] of this.alterationsCopy.entries()) {
                if(alteration.detailedViews?.length) {
                    let hasCachedMedias = false
                    alteration.detailedViews = alteration.detailedViews.filter(mediaId => {
                        if(mediaId === failedUpload.cacheKey) {
                            return false
                        }

                        if(MediaTransferHelper.isLocalMedia(mediaId)) {
                            hasCachedMedias = true
                        }
                        return true
                    })

                    if(hasCachedMedias) {
                        alterationsWithFailedUpload.push(i + 1)
                    }
                }
            }

            this.failedImageUploads.splice(index, 1)
            this.uploadFailureDescription = this.computeUploadFailureDescription(alterationsWithFailedUpload)
        },

        computeUploadFailureDescription(alterationsWithFailedUpload: number[]): string {
            let alterationsString = ''
            for(const alterationIndex of _.uniq(alterationsWithFailedUpload)) {
                alterationsString += (alterationIndex + (this.firstAlterationIndex as number)) + ', '
            }
            alterationsString = alterationsString.slice(0, -2)

            return this.$t(
                'drawingEditor.errors.failedUploadDetailedImageDescription',
                [
                    this.failedImageUploads.length,
                    alterationsString,
                ]
            ) as string
        },

        unsubscribe() {
            this.unsubscribeFrom('annotationTitle');
            this.unsubscribeFrom('media');
        },

        setupMedia(mediaSrc) {
            // strip exif rotation data and rotate media
            let base64Media: string = mediaSrc
            const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(mediaSrc)
            if(orientation != -1) {
                if(mediaWithoutOrientation instanceof String || typeof mediaWithoutOrientation === 'string') {
                    base64Media = mediaWithoutOrientation as string
                } else {
                    base64Media = arrayBufferToBase64(mediaWithoutOrientation)
                }
            }

            this.image.src = base64Media
            this.image.onload = () => {
                this.fixImageOrientation(
                    {
                        height: this.image.height,
                        image: this.image,
                        width: this.image.width,
                        x: 0,
                        y: 0,
                    },
                    orientation != -1 ? orientation : undefined
                )

                this.stage = (this.$refs.stage as any).getStage()
                this.container = this.stage.container()
                const transformerNode = (this.$refs.transformer as any).getNode()
                const imageBackground = (this.$refs.backgroundImage as any).getNode()
                transformerNode.nodes([imageBackground])

                // Force canvas resizing and redrawing after Vue render
                this.$nextTick().then(() => this.resizeStage())
                // Listening to window resize event to trigger stage redrawing
                window.addEventListener('resize', this.resizeStage)
            }
        },

        /**
         * Adapts the orientation of the image according to the
         * EXIF orientation metadata
         * @param baseImageConfig
         */
        async fixImageOrientation(baseImageConfig, orientation?: number) {
            if(orientation) {
                this.orientation = orientation
            } else {
                this.orientation = isImageOrientationAutoManaged() ? 1 : await getImageOrientation(baseImageConfig.image)
            }
            switch (this.orientation) {
                case 3:
                    this.configImage = {
                        ...baseImageConfig,

                        offsetX: baseImageConfig.width,
                        offsetY: baseImageConfig.height,
                        rotation: 180,
                    }
                    this.editorOrientationClass = "drawing-editor-h"
                    this.canvasOrientationClass = "drawing-canvas-h"
                    break;
                case 6:
                    this.configImage = {
                        ...baseImageConfig,

                        offsetX: 0,
                        offsetY: baseImageConfig.height,
                        rotation: 90,
                    }
                    this.editorOrientationClass = "drawing-editor-v"
                    this.canvasOrientationClass = "drawing-canvas-v"
                    break;
                case 8:
                    this.configImage = {
                        ...baseImageConfig,

                        offsetX: baseImageConfig.width,
                        offsetY: 0,
                        rotation: -90,
                    }
                    this.editorOrientationClass = "drawing-editor-v"
                    this.canvasOrientationClass = "drawing-canvas-v"
                    break;

                default:
                    if (this.image.height > this.image.width) {
                        this.configImage = baseImageConfig;
                        this.editorOrientationClass = "drawing-editor-v"
                        this.canvasOrientationClass = "drawing-canvas-v"
                    } else {
                        this.configImage = baseImageConfig;
                        this.editorOrientationClass = "drawing-editor-h"
                        this.canvasOrientationClass = "drawing-canvas-h"
                    }
                    break;
            }
        },

        /**
         * Allows to make the Konva stage fill the window properly
         */
        resizeStage() {
            if (this.stage && this.container) {

                this.adaptCanvasSizeToImageOrientation()

                // Take orientation into account to retrieve real width of image (chrome 81 issue)
                const refDimension = this.orientation === 6 || this.orientation === 8 ? this.image.width : this.image.height

                // setting drawing values for responsive behavior
                this.strokeWidth = this.STROKE_WIDTH_RATIO * refDimension;
                this.arrowPointerLength = this.ARROW_POINTER_LENGTH_RATIO * refDimension;
                this.arrowPointerWidth = this.ARROW_POINTER_WIDTH_RATIO * refDimension;
                this.annotationLabelConfig.fontSize = this.ANNOTATION_LABEL_FONT_SIZE_RATIO * refDimension;
                this.annotationLabelConfig.radius = this.ANNOTATION_LABEL_RADIUS_RATIO * refDimension;
                this.annotationLabelConfig.strokeWidth = this.ANNOTATION_LABEL_STROKE_WIDTH_RATIO * refDimension;
                //this.freeDrawingSimplificationFactor = this.FREE_DRAWING_SIMPLIFICATION_FACTOR_RATIO * refDimension;

                console.debug("Drawing editor, config konva: ", this.configKonva)
                console.debug("Drawing editor, config image: ", this.configImage)
                console.debug("Drawing editor, stage scale: ", this.stage.scale())
                // Make editor display only when resizing stuff is done
                this.ready = true
            }
        },

        /**
         * Makes the canvas fit the contained image, taking the
         * image orientation into account
         */
        adaptCanvasSizeToImageOrientation() {
            const containerSize = {
                height: this.container.clientHeight,
                width: this.container.clientWidth
            };
            let imageRatio = 1;
            switch (this.orientation) {
                // Vertical images
                case 6:
                case 8:
                    {
                        this.stageScaleFactor = containerSize.height / this.image.width;
                        imageRatio = this.image.width / this.image.height;
                        this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                        this.configKonva.height = containerSize.height;
                        this.configKonva.width = this.configKonva.height / imageRatio;
                        break;
                    }
                // Horizontal images
                default:
                    {
                        // Vertical images with orientation 1
                        if (this.image.height > this.image.width) {
                            this.stageScaleFactor = containerSize.height / this.image.height;
                            imageRatio = this.image.height / this.image.width;
                            this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                            this.configKonva.height = containerSize.height;
                            this.configKonva.width = this.configKonva.height / imageRatio;
                        } else {
                            this.stageScaleFactor = containerSize.width / this.image.width;
                            imageRatio = this.image.width / this.image.height;
                            this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                            this.configKonva.width = containerSize.width;
                            this.configKonva.height = this.configKonva.width / imageRatio;
                            break;
                        }
                    }
            }
        },

        /**
         * Renumbers all drawings from 0 to avoid conflicts in indices
         */
        renumberShapes() {
            const theRealThis = this
            this.drawings.map((shapes, index) => {
                for(const [index2, shape] of shapes.entries()) {
                    //this.chooseTool(shape.type)
                    shape.id = theRealThis.currentTool.generateShapeId(index, index2)
                }
            })
            this.toolBox.clear()
        },

        onRadiusUpdated(value: number | String) {
            try {
                let radius: number
                if(typeof value === "string" || value instanceof String) {
                    radius = parseInt(value as string)
                } else {
                    radius = value
                }

                this.eraserOutlineConfig.radius = Math.round(radius)
                this.eraserRadius = radius

                if(this.currentToolType == DrawingToolType.ERASER) {
                    this.changeDrawingTool(DrawingToolType.ERASER)
                    this.chooseTool(DrawingToolType.ERASER)
                }
            } catch(err) {
                console.debug("Drawing area: Failed to change radius of eraser tool")
            }
        },

        onScrollZoom(event) {
            this.ready = false

            // stop default scrolling
            event.evt.preventDefault()
            this.stage = (this.$refs.stage as any).getStage()

            const oldScale = this.stage.scaleX()
            const pointer = this.stage.getPointerPosition()

            const mousePointTo = {
                x: (pointer.x - this.stage.x()) / oldScale,
                y: (pointer.y - this.stage.y()) / oldScale,
            }

            // how to scale? Zoom in? Or zoom out?
            let direction = event.evt.deltaY > 0 ? ZoomDirection.IN : ZoomDirection.OUT
            this.scrollZoom(mousePointTo, direction)
        },

        scrollZoom(zoomCoordinate: {x: number, y: number}, zoomDirection: ZoomDirection, scaleFactor: number = DEFAULT_ZOOM_SCALE) {
            this.stage = (this.$refs.stage as any).getStage()
            const oldScale = this.stage.scaleX()
            const pointer = this.stage.getPointerPosition()

            let newScale: number
            if(zoomDirection === ZoomDirection.IN) {
                newScale = oldScale * scaleFactor
            } else {
                newScale = oldScale / scaleFactor
            }

            const theRealThis = this

            this.$nextTick().then(() => {
                theRealThis.stage.scale({ x: newScale, y: newScale })
                const newPos = {
                    x: pointer.x - zoomCoordinate.x * newScale,
                    y: pointer.y - zoomCoordinate.y * newScale,
                }
                theRealThis.stage.position(newPos)
                const transformerNode = (this.$refs.transformer as any).getNode()
                transformerNode.forceUpdate()
                theRealThis.ready = true
            })
        },

        pinchZoom(touch1, touch2) {
            const theRealThis = this

            if (this.stage.isDragging()) {
                this.stage.stopDrag()
            }

            const rect = this.stage.container().getBoundingClientRect()
            const p1 = {
                x: touch1.clientX - rect.left,
                y: touch1.clientY - rect.top,
            }
            const p2 = {
                x: touch2.clientX - rect.left,
                y: touch2.clientY - rect.top,
            }

            const pinchCenter = this.getPinchCenter(p1, p2)

            if (!this.lastPinchCenter) {
                this.lastPinchCenter = pinchCenter
                return
            }

            const pinchDistance = this.getPinchDistance(p1, p2)

            if (!this.lastPinchDistance) {
                this.lastPinchDistance = pinchDistance
                return
            }

            // local coordinates of center point
            const pointTo = {
                x: (pinchCenter.x - this.stage.x()) / this.stage.scaleX(),
                y: (pinchCenter.y - this.stage.y()) / this.stage.scaleX(),
            }

            const scale = this.stage.scaleX() * (pinchDistance / this.lastPinchDistance)

            this.$nextTick().then(() => {
                theRealThis.stage = (theRealThis.$refs.stage as any).getStage()

                theRealThis.stage.scaleX(scale)
                theRealThis.stage.scaleY(scale)

                // calculate new position of the stage
                const dx = pinchCenter.x - theRealThis.lastPinchCenter!.x
                const dy = pinchCenter.y - theRealThis.lastPinchCenter!.y

                const newPos = {
                    x: pinchCenter.x - pointTo.x * scale + dx,
                    y: pinchCenter.y - pointTo.y * scale + dy,
                }

                theRealThis.stage.position(newPos)
                theRealThis.stage.batchDraw() // important, this line refresh the stage after zoom

                theRealThis.lastPinchDistance = pinchDistance
                theRealThis.lastPinchCenter = pinchCenter
            })
        },

        getPinchCenter(p1, p2) {
            return {
                x: (p1.x + p2.x) / 2,
                y: (p1.y + p2.y) / 2,
            }
        },

        getPinchDistance(p1, p2) {
            return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
        },

        refreshTransformer() {
            const transformer = (this.$refs.transformer as any).getNode()

            this.$nextTick(() => {
                const children = this.stage.getChildren(node => {
                    return node.getClassName() !== 'Stage' && node.getClassName() !== 'Layer'
                })
                transformer.nodes(children)
            })
        },

        getTransformedPoint(point: {x: number, y: number}): {x: number, y: number} {
            const transform = this.stage.getAbsoluteTransform().copy()
            return transform.invert().point(point)
        },

        onTouchStart(event) {
            if(!this.readOnly) {
                if (event.currentTarget._pointerPositions) {
                    this.startClick = {
                        x: event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x,
                        y: event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y,
                    }

                    this.chooseTool(this.currentToolType)

                    const transformedStartClick = this.getTransformedPoint(this.startClick)

                    if(this.currentToolType !== DrawingToolType.HAND) {
                        let newShape = this.currentTool.addShape(
                            transformedStartClick.x,
                            transformedStartClick.y,
                            this.drawings.length,
                            this.strokeColor
                        )

                        if(this.currentToolType !== DrawingToolType.ERASER) {
                            if(this.isDrawingFirstShape) {
                                this.currentDrawing = [newShape]

                                this.addAlteration()
                                this.drawings.push(this.currentDrawing)
                                if(this.currentToolType === DrawingToolType.FREE_DRAWING) {
                                    this.isDrawingFirstShape = false
                                }

                                this.refreshTransformer()
                            } else {
                                this.currentDrawing.push(newShape)
                            }

                        } else {
                            if(DEBUG_SHOW_ERASER_AREA) {
                                this.currentDebugDrawing = [newShape]
                                this.debugDrawings.push(this.currentDebugDrawing)

                                this.refreshTransformer()
                            }
                        }
                    }

                    this.isDrawing = true;
                } else {
                    this.startClick = undefined
                }
            }
        },

        onTouchMove(event) {
            const touch1 = event.evt.touches[0]
            const touch2 = event.evt.touches.length > 1 ? event.evt.touches[1] : null

            if(touch1 && !touch2) {
                if(!this.readOnly && this.currentToolType != DrawingToolType.HAND) {
                    if (this.isDrawing && event.currentTarget._changedPointerPositions) {
                        const currentPos = {
                            x: event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x,
                            y: event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y,
                        }

                        const scaledCurrentPos = this.getTransformedPoint(currentPos)
                        const scaledStartClick = this.getTransformedPoint(this.startClick)

                        let shape = this.currentDrawing[this.currentDrawing.length - 1]
                        if(this.currentToolType === DrawingToolType.ERASER) {
                            shape = this.currentDrawing
                            this.eraserOutlineConfig.x = scaledCurrentPos.x
                            this.eraserOutlineConfig.y = scaledCurrentPos.y
                        }
                        this.currentTool.onTouchMove(
                            shape,
                            scaledCurrentPos.x,
                            scaledCurrentPos.y,
                            scaledStartClick.x,
                            scaledStartClick.y,
                            this.currentDebugDrawing
                        )
                    }
                }
            }
            else if(touch1 && touch2) {
                if(this.currentToolType === DrawingToolType.HAND) {
                    this.pinchZoom(touch1, touch2)
                }
            }
        },

        onTouchEnd(event) {
            this.lastPinchDistance = null
            this.lastPinchCenter = null

            if(!this.readOnly) {
                if(this.currentToolType != DrawingToolType.HAND) {
                    if (this.startClick && event.currentTarget._changedPointerPositions) {
                        const endPos = {
                            x: event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x,
                            y: event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y,
                        }

                        const scaledEndPos = this.getTransformedPoint(endPos)
                        const scaledStartClick = this.getTransformedPoint(this.startClick)

                        const shape = this.currentDrawing[this.currentDrawing.length - 1]
                        this.currentTool.onTouchEnd(
                            shape,
                            scaledEndPos.x,
                            scaledEndPos.y,
                            scaledStartClick.x,
                            scaledStartClick.y
                        )
                    }
                }
                this.isDrawing = false
            }
        },

        onValidateDrawing() {
            this.isDrawingFirstShape = true
        },

        isToolSelected(tool: DrawingToolType) {
            return this.currentToolType === tool;
        },

        changeDrawingTool(tool: DrawingToolType) {
            this.currentToolType = tool
        },

        chooseTool(toolType: DrawingToolType) {
            switch (toolType) {
                case DrawingToolType.ARROW:
                    if(!this.toolBox.get(toolType)) {
                        this.toolBox.set(toolType, new ArrowDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR, this.arrowPointerLength, this.arrowPointerWidth))
                    }
                    break
                case DrawingToolType.CIRCLE:
                    if(!this.toolBox.get(toolType)) {
                        this.toolBox.set(toolType, new CircleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR))
                    }
                    break
                case DrawingToolType.RECTANGLE:
                    if(!this.toolBox.get(toolType)) {
                        this.toolBox.set(toolType, new RectangleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR))
                    }
                    break
                case DrawingToolType.FREE_DRAWING:
                    if(!this.toolBox.get(toolType)) {
                        this.toolBox.set(
                            toolType,
                            new FreeDrawingTool(
                                this.strokeWidth,
                                this.DEFAULT_STROKE_COLOR,
                                false,
                                //this.freeDrawingSimplificationFactor
                            )
                        )
                    }
                    break
                case DrawingToolType.ERASER:
                    this.toolBox.set(
                        toolType,
                        this.getNewEraserTool()
                    )
                    break
                case DrawingToolType.HAND:
                    if(!this.toolBox.get(toolType)) {
                        this.toolBox.set(
                            toolType,
                            new HandDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR)
                        )
                    }
                    break
            }
            const tool = this.toolBox.get(toolType)
            if (tool) {
                this.currentTool = tool
            }
        },

        getNewEraserTool(): EraserDrawingTool {
            return new EraserDrawingTool(
                this.strokeWidth,
                this.DEFAULT_STROKE_COLOR,
                this.eraserRadius
            )
        },
    },
    watch: {
        drawings: {
            handler() {
                // Update drawings
                for(let i=0; i<this.drawings.length; i++) {
                    _.set(this.alterationsCopy[i], 'drawing', JSON.stringify(this.drawings[i]))
                }
            },
            deep: true,
        },
        // `stage` is (re)assigned in several places (initial load, rotation/retry); watch it here
        // rather than repeating the bind at each site. Bound via Konva's own event API, not
        // template @mousedown, since @vue/compat MODE 2 excludes listeners from a component's $attrs.
        stage(newStage, oldStage) {
            if (oldStage) {
                oldStage.off('mousedown touchstart mouseup touchend mousemove touchmove wheel')
            }
            if (newStage) {
                newStage.on('mousedown touchstart', this.onTouchStart)
                newStage.on('mouseup touchend', this.onTouchEnd)
                newStage.on('mousemove touchmove', this.onTouchMove)
                newStage.on('wheel', this.onScrollZoom)
            }
        },
    },
    beforeUnmount() {
        this.stage?.off('mousedown touchstart mouseup touchend mousemove touchmove wheel')
    },
})
